import { AITool } from '../types'

export const realPlaceOrderTool: AITool = {
  name: 'place_order',
  description: 'Place a real trading order with visual demonstration on the actual BuySell component',
  category: 'trading',
  parameters: [
    {
      name: 'symbol',
      type: 'string',
      description: 'Trading pair symbol (e.g., BTCUSDT, ETHUSDT)',
      required: true
    },
    {
      name: 'side',
      type: 'string',
      description: 'Order side: BUY or SELL',
      required: true,
      enum: ['BUY', 'SELL']
    },
    {
      name: 'quantity',
      type: 'number',
      description: 'Amount to trade (in base currency, e.g., 0.5 BTC)',
      required: false
    },
    {
      name: 'total',
      type: 'number',
      description: 'Total amount to trade (in quote currency, e.g., 1000 USDT worth of BTC)',
      required: false
    },
    {
      name: 'type',
      type: 'string',
      description: 'Order type: MARKET, LIMIT, or STOP',
      required: false,
      default: 'MARKET',
      enum: ['MARKET', 'LIMIT', 'STOP']
    },
    {
      name: 'price',
      type: 'number',
      description: 'Price per unit (required for LIMIT orders, optional for STOP orders to make stop-limit)',
      required: false
    },
    {
      name: 'triggerPrice',
      type: 'number',
      description: 'Trigger price for STOP orders (required when type is STOP)',
      required: false
    }
  ],
  examples: [
    'Place a buy order for 0.5 BTCUSDT',
    'Sell 1000 USDT worth of ETH at market price',
    'Buy 500 USDT worth of BTC',
    'Place a limit buy order for 2 ETH at $3000',
    'Place a limit sell order for 1000 USDT worth of SOL at $180',
    'Place a stop sell order for 1 BTC with trigger at $45000',
    'Place a stop or stop-limit buy order for 0.5 BTC with trigger at $46000 and limit at $46500',
    'Place a stop buy order for 500 USDT worth of ETH with trigger at $3200'
  ],
  execute: async (params, context) => {
    console.log('💰 Real Trading: Executing order with params:', params)
    
    // Validate that either quantity OR total is provided, but not both or neither
    const hasQuantity = params.quantity !== undefined && params.quantity !== null
    const hasTotal = params.total !== undefined && params.total !== null
    
    if (!hasQuantity && !hasTotal) {
      return {
        success: false,
        error: 'Either quantity (base currency amount) or total (quote currency amount) must be provided'
      }
    }
    
    if (hasQuantity && hasTotal) {
      return {
        success: false,
        error: 'Please provide either quantity OR total, not both'
      }
    }
    
    // Trigger visual trading through a global event
    const tradingEvent = new CustomEvent('realPlaceOrder', {
      detail: params
    })
    
    // Set up a promise to listen for the result
    const resultPromise = new Promise((resolve) => {
      const handleResult = (event: CustomEvent) => {
        console.log('📈 Received real trading result:', event.detail)
        window.removeEventListener('realOrderResult', handleResult as EventListener)
        resolve(event.detail)
      }
      window.addEventListener('realOrderResult', handleResult as EventListener)
      
      // Timeout after 30 seconds for real trading
      setTimeout(() => {
        window.removeEventListener('realOrderResult', handleResult as EventListener)
        resolve({
          success: false,
          error: 'Order timeout - BuySell component not responding',
          ...params,
          timestamp: new Date()
        })
      }, 30000)
    })
    
    // Dispatch the event
    window.dispatchEvent(tradingEvent)
    
    // Wait for the result
    const result = await resultPromise
    
    return {
      success: true,
      result,
      message: `Real trading order ${(result as any)?.success ? 'executed successfully' : 'failed'}`
    }
  }
}

export const buySellTools: AITool[] = [
  realPlaceOrderTool
] 